/**
 * Exact decimal-string arithmetic.
 *
 * The API speaks decimal strings for unit prices, quantities and rates
 * ("49.99", "0.0025", "150") and integer minor units for posted amounts.
 * Nothing here converts through a float: values are scaled BigInts.
 */

const DECIMAL_RE = /^([+-])?(\d*)(?:\.(\d*))?$/;

export interface ParsedDecimal {
  negative: boolean;
  /** Digits without a decimal point. */
  digits: bigint;
  /** Number of fractional digits represented in `digits`. */
  scale: number;
}

export function parseDecimal(input: string): ParsedDecimal | null {
  const trimmed = input.trim();
  const match = DECIMAL_RE.exec(trimmed);
  if (!match) return null;
  const intPart = match[2] ?? '';
  const fracPart = match[3] ?? '';
  if (intPart === '' && fracPart === '') return null;
  const digits = BigInt((intPart || '0') + fracPart);
  return { negative: match[1] === '-' && digits !== 0n, digits, scale: fracPart.length };
}

export function isDecimalString(input: string): boolean {
  return parseDecimal(input) !== null;
}

function toSigned(d: ParsedDecimal): bigint {
  return d.negative ? -d.digits : d.digits;
}

function rescale(value: bigint, from: number, to: number): bigint {
  return to >= from ? value * 10n ** BigInt(to - from) : value / 10n ** BigInt(from - to);
}

/** Render a scaled integer as a canonical decimal string (no trailing zeros). */
export function scaledToString(value: bigint, scale: number, minScale = 0): string {
  const negative = value < 0n;
  const abs = negative ? -value : value;
  let text = abs.toString().padStart(scale + 1, '0');
  let int = scale > 0 ? text.slice(0, -scale) : text;
  let frac = scale > 0 ? text.slice(-scale) : '';
  frac = frac.replace(/0+$/, '');
  if (frac.length < minScale) frac = frac.padEnd(minScale, '0');
  int = int.replace(/^0+(?=\d)/, '');
  text = frac ? `${int}.${frac}` : int;
  return negative && /[1-9]/.test(text) ? `-${text}` : text;
}

/** "049.9900" → "49.99"; "150.000000000000000000" → "150". Returns the input when it is not a decimal. */
export function normalizeDecimal(input: string, minScale = 0): string {
  const parsed = parseDecimal(input);
  if (!parsed) return input;
  return scaledToString(toSigned(parsed), parsed.scale, minScale);
}

export function compareDecimal(a: string, b: string): -1 | 0 | 1 {
  const pa = parseDecimal(a);
  const pb = parseDecimal(b);
  if (!pa || !pb) throw new TypeError(`Not a decimal: ${!pa ? a : b}`);
  const scale = Math.max(pa.scale, pb.scale);
  const va = rescale(toSigned(pa), pa.scale, scale);
  const vb = rescale(toSigned(pb), pb.scale, scale);
  return va === vb ? 0 : va < vb ? -1 : 1;
}

export function addDecimal(a: string, b: string): string {
  const pa = parseDecimal(a);
  const pb = parseDecimal(b);
  if (!pa || !pb) throw new TypeError(`Not a decimal: ${!pa ? a : b}`);
  const scale = Math.max(pa.scale, pb.scale);
  return scaledToString(
    rescale(toSigned(pa), pa.scale, scale) + rescale(toSigned(pb), pb.scale, scale),
    scale,
  );
}

export function multiplyDecimal(a: string, b: string): string {
  const pa = parseDecimal(a);
  const pb = parseDecimal(b);
  if (!pa || !pb) throw new TypeError(`Not a decimal: ${!pa ? a : b}`);
  return scaledToString(toSigned(pa) * toSigned(pb), pa.scale + pb.scale);
}

export type RoundingMode = 'half-up' | 'half-even' | 'down';

/**
 * Round a decimal string to `scale` fractional digits and return the scaled
 * integer (e.g. scale 2 → minor units). Half-up rounds away from zero, as
 * the API's MinorUnits does by default.
 */
export function roundDecimalToScaled(input: string, scale: number, mode: RoundingMode = 'half-up'): bigint {
  const parsed = parseDecimal(input);
  if (!parsed) throw new TypeError(`Not a decimal: ${input}`);
  if (parsed.scale <= scale) return rescale(toSigned(parsed), parsed.scale, scale);
  const divisor = 10n ** BigInt(parsed.scale - scale);
  const quotient = parsed.digits / divisor;
  const remainder = parsed.digits % divisor;
  let rounded = quotient;
  if (mode !== 'down') {
    const twice = remainder * 2n;
    if (twice > divisor || (twice === divisor && (mode === 'half-up' || quotient % 2n === 1n))) {
      rounded = quotient + 1n;
    }
  }
  return parsed.negative ? -rounded : rounded;
}

/**
 * Clean free-text numeric input ("1,234.50 ", "$12") into a decimal string,
 * or null when it is not a number. Commas are treated as grouping
 * separators and "." as the decimal separator.
 */
export function sanitizeDecimalInput(raw: string): string | null {
  const cleaned = raw.replace(/[\s,_$€£¥]/g, '');
  if (cleaned === '' || !isDecimalString(cleaned)) return null;
  return normalizeDecimal(cleaned);
}

export function decimalScale(input: string): number {
  return parseDecimal(input)?.scale ?? 0;
}
